/**
 * O registro do service worker — `PWA §4.6`, `§5.2`, `§5.5`.
 *
 * O registro e **nosso** (`injectRegister: false` no `vite.config.ts`) porque tres decisoes
 * moram aqui e nenhuma delas e expressavel na configuracao do plugin:
 *
 * 1. **`updateViaCache: 'none'`** — pre-requisito absoluto do kill switch (`PWA §4.6`, PWA-016).
 *    Sem isso, o navegador pode servir um `sw.js` cacheado por ate 24 h, e o **tombstone nao
 *    chega**. Combina com `Cache-Control: no-cache` no proprio `sw.js`, que e responsabilidade
 *    do servidor e e verificada por smoke pos-deploy.
 * 2. **`update()` com throttle de 60 min** (PWA-013), em `visibilitychange -> visible`. Nao a
 *    cada navegacao: em SPA, navegacao e troca de rota, e uma requisicao por clique e franquia
 *    de quem conta MB.
 * 3. **`controllerchange` recarrega uma unica vez.** Sem a trava, um `skipWaiting` durante um
 *    reload produz laco de recarga — o incidente que parece "o app nao abre".
 */

import { criarAceleradorDeVerificacao } from './atualizacao.js';
import { MENSAGEM_SKIP_WAITING } from '../sw/mensagens.js';

export interface RegistroDoSw {
  /** Ha um SW em `waiting`? A UI usa isto para decidir se ha aviso a mostrar. */
  readonly temVersaoNova: () => boolean;
  /** Manda `SKIP_WAITING` ao worker em espera. Chamado **so** por toque do usuario (PWA-010). */
  readonly aplicar: () => void;
  readonly desmontar: () => void;
}

export interface OpcoesDeRegistro {
  readonly caminhoDoSw?: string;
  /** Chamado quando um worker entra em `waiting`. */
  readonly aoDetectarVersaoNova?: () => void;
  readonly agora?: () => number;
}

/**
 * Registra o service worker e liga o ciclo de atualizacao.
 *
 * Devolve `null` quando nao ha `serviceWorker` (Safari em contexto inseguro, navegador antigo,
 * jsdom). O app **funciona sem SW** — perde offline e precache, nao perde funcao: essa e a razao
 * de o `web-interno` poder simplesmente nao ter um (ADR-0017).
 */
export function registrarServiceWorker(opcoes: OpcoesDeRegistro = {}): RegistroDoSw | null {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;

  const caminho = opcoes.caminhoDoSw ?? '/sw.js';
  const agora = opcoes.agora ?? (() => Date.now());
  const acelerador = criarAceleradorDeVerificacao();

  let registro: ServiceWorkerRegistration | null = null;
  let recarregando = false;

  const aoTrocarControlador = (): void => {
    // A trava e o que impede o laco de recarga.
    if (recarregando) return;
    recarregando = true;
    window.location.reload();
  };

  const aoMudarVisibilidade = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (!acelerador.podeVerificar(agora())) return;
    void registro?.update();
  };

  void navigator.serviceWorker
    .register(caminho, {
      type: 'module',
      // PWA-016 / `PWA §4.6` — sem isto, o kill switch nao existe.
      updateViaCache: 'none',
    })
    .then((registrado) => {
      registro = registrado;
      registrado.addEventListener('updatefound', () => {
        const instalando = registrado.installing;
        instalando?.addEventListener('statechange', () => {
          if (instalando.state === 'installed' && navigator.serviceWorker.controller !== null) {
            opcoes.aoDetectarVersaoNova?.();
          }
        });
      });
    })
    .catch((erro: unknown) => {
      // Falhar o registro **nao** pode derrubar o app: sem SW ele perde offline, nao funcao.
      console.warn('[pwa] registro do service worker falhou', erro);
    });

  navigator.serviceWorker.addEventListener('controllerchange', aoTrocarControlador);
  document.addEventListener('visibilitychange', aoMudarVisibilidade);

  return {
    temVersaoNova: () => registro?.waiting != null,
    aplicar: () => {
      registro?.waiting?.postMessage({ type: MENSAGEM_SKIP_WAITING });
    },
    desmontar: () => {
      navigator.serviceWorker.removeEventListener('controllerchange', aoTrocarControlador);
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
    },
  };
}
